import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  User, 
  Phone, 
  Truck,
  Building2,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  Download,
  Edit,
  DollarSign,
  MessageSquare,
  Send,
  Camera,
  StopCircle,
  Play,
  Gauge,
  Timer,
  TrendingUp,
  FileDown,
  Image,
  Trash2,
  Eye,
  Volume2,
  Star
} from 'lucide-react';
import PrestadorProfileView from '../components/PrestadorProfileView';
import { formatEta } from '../lib/date';

const statusConfig = {
  criada: { label: 'Criada', color: 'bg-blue-100 text-blue-700' },
  aguardando_inicio: { label: 'Aguardando Início', color: 'bg-yellow-100 text-yellow-700' },
  em_andamento: { label: 'Em Andamento', color: 'bg-orange-100 text-orange-700' },
  finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  aguardando_validacao: { label: 'Aguardando Validação', color: 'bg-purple-100 text-purple-700' },
  contestada: { label: 'Contestada', color: 'bg-amber-100 text-amber-700' },
  aprovado_faturamento: { label: 'Aprovado p/ Faturamento', color: 'bg-emerald-100 text-emerald-700' },
  faturado: { label: 'Faturado', color: 'bg-slate-100 text-slate-700' },
};

const MissionDetailPage = () => {
  const { id } = useParams();
  const { token, hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [editMode, setEditMode] = useState(false);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [chatSubTab, setChatSubTab] = useState('messages');
  const chatEndRef = useRef(null);
  const pollingRef = useRef(null);
  const wsRef = useRef(null);
  
  // Odometer state
  const [odometerStart, setOdometerStart] = useState('');
  const [odometerEnd, setOdometerEnd] = useState('');
  const [arrivedAt, setArrivedAt] = useState('');
  const [savingOdometer, setSavingOdometer] = useState(false);
  
  // Supplier responses state
  const [supplierResponses, setSupplierResponses] = useState([]);

  // Cancel and Contest dialogs
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showContestDialog, setShowContestDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [contestReason, setContestReason] = useState('');
  
  // Edit mode for contested missions
  const [editFormData, setEditFormData] = useState({
    reason_description: '',
    location_address: '',
    vehicle_plate: '',
    vehicle_color: '',
    driver_name: '',
    driver_phone: '',
    observations: ''
  });
  
  // AI Report
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportContent, setReportContent] = useState('');

  // Prestador rating
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [showPrestadorProfile, setShowPrestadorProfile] = useState(false);
  
  // Ref for supplier responses polling
  const responsesPollingRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [id, token]);

  // Poll for supplier responses when mission is in 'criada' status and was notified
  useEffect(() => {
    if (mission?.status !== 'criada' || mission?.supplier_id) {
      if (responsesPollingRef.current) {
        clearInterval(responsesPollingRef.current);
        responsesPollingRef.current = null;
      }
      return;
    }
    
    // Only poll if mission was notified via WhatsApp
    if (!mission?.whatsapp_notified_at) return;
    
    const pollResponses = async () => {
      try {
        const data = await api.get(`/missions/${id}/prestador-responses`, token);
        setSupplierResponses(data.responses || []);
      } catch (error) {
        console.error('Error polling supplier responses:', error);
      }
    };
    
    pollResponses();
    responsesPollingRef.current = setInterval(pollResponses, 3000);
    
    return () => {
      if (responsesPollingRef.current) {
        clearInterval(responsesPollingRef.current);
        responsesPollingRef.current = null;
      }
    };
  }, [id, token, mission?.status, mission?.supplier_id, mission?.whatsapp_notified_at]);

  // Poll for new chat messages when on chat tab
  useEffect(() => {
    if (activeTab !== 'chat' || !mission?.supplier_id) return;
    
    const pollChat = async () => {
      try {
        const messages = await api.get(`/whatsapp/chat/${id}`, token);
        setChatMessages(messages);
        
        // Mark messages as read
        await api.post(`/whatsapp/mark-read/${id}`, {}, token);
      } catch (error) {
        console.error('Error polling chat:', error);
      }
    };
    
    pollChat();
    pollingRef.current = setInterval(pollChat, 3000);
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [activeTab, id, token, mission?.supplier_id]);

  // Real-time chat sync via WebSocket (falls back to the 3s polling above if it drops)
  useEffect(() => {
    if (activeTab !== 'chat' || !mission?.supplier_id) {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      return;
    }
    const wsBase = (process.env.REACT_APP_BACKEND_URL || '').replace('https://', 'wss://').replace('http://', 'ws://');
    let closedIntentionally = false;
    let reconnectDelay = 1000;
    let reconnectTimer = null;
    let heartbeat = null;

    const connect = () => {
      const ws = new WebSocket(`${wsBase}/api/ws/mission/${id}/chat?token=${token}`);
      ws.onopen = () => {
        reconnectDelay = 1000;
        heartbeat = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 25000);
      };
      ws.onmessage = async () => {
        try {
          const messages = await api.get(`/whatsapp/chat/${id}`, token);
          setChatMessages(messages);
          await api.post(`/whatsapp/mark-read/${id}`, {}, token);
        } catch (error) {}
      };
      ws.onclose = () => {
        if (heartbeat) clearInterval(heartbeat);
        if (!closedIntentionally) {
          reconnectTimer = setTimeout(connect, reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 2, 10000);
        }
      };
      ws.onerror = () => { ws.close(); };
      wsRef.current = ws;
    };
    connect();

    return () => {
      closedIntentionally = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeat) clearInterval(heartbeat);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, token, mission?.supplier_id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const fetchData = async () => {
    try {
      const [missionData, suppliersData] = await Promise.all([
        api.get(`/missions/${id}`, token),
        api.get('/suppliers', token)
      ]);
      setMission(missionData);
      setSuppliers(suppliersData);
      setSelectedSupplier(missionData.supplier_id || '');
      
      // Load odometer data
      setOdometerStart(missionData.odometer_start || '');
      setOdometerEnd(missionData.odometer_end || '');
      setArrivedAt(missionData.supplier_arrived_at ? 
        new Date(missionData.supplier_arrived_at).toISOString().slice(0, 16) : '');
      
      // Load supplier responses if mission was notified and no supplier selected yet
      if (!missionData.supplier_id && missionData.whatsapp_notified_at) {
        try {
          const data = await api.get(`/missions/${id}/prestador-responses`, token);
          setSupplierResponses(data.responses || []);
        } catch (e) {
          console.log('No supplier responses yet');
        }
      }
      
      // Load chat messages if supplier is assigned
      if (missionData.supplier_id) {
        try {
          const messages = await api.get(`/whatsapp/chat/${id}`, token);
          setChatMessages(messages);
        } catch (e) {
          console.log('No chat messages yet');
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar missão');
      navigate('/missions');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await api.put(`/missions/${id}`, { status: newStatus }, token);
      await fetchData();
      toast.success('Status atualizado com sucesso');
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdating(false);
    }
  };

  const assignSupplier = async () => {
    if (!selectedSupplier) {
      toast.error('Selecione um fornecedor');
      return;
    }
    setUpdating(true);
    try {
      await api.put(`/missions/${id}`, { 
        supplier_id: selectedSupplier,
        status: 'em_andamento',
        started_at: new Date().toISOString(),
        supplier_accepted_at: new Date().toISOString()
      }, token);
      await fetchData();
      toast.success('Fornecedor atribuído e missão iniciada');
    } catch (error) {
      toast.error('Erro ao atribuir fornecedor');
    } finally {
      setUpdating(false);
    }
  };

  const selectSupplierFromResponse = async (prestadorId) => {
    setUpdating(true);
    try {
      await api.post(`/missions/${id}/select-prestador`, { prestador_id: prestadorId }, token);
      await fetchData();
      toast.success('Prestador selecionado! Demais prestadores foram dispensados.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao selecionar prestador');
    } finally {
      setUpdating(false);
    }
  };

  const openEditMode = () => {
    setEditFormData({
      reason_description: mission.reason_description || '',
      location_address: mission.location_address || '',
      vehicle_plate: mission.vehicle_plate || '',
      vehicle_color: mission.vehicle_color || '',
      driver_name: mission.driver_name || '',
      driver_phone: mission.driver_phone || '',
      observations: mission.observations || ''
    });
    setEditMode(true);
  };

  const saveEditedMission = async () => {
    setUpdating(true);
    try {
      await api.put(`/missions/${id}`, editFormData, token);
      await fetchData();
      toast.success('Missão atualizada com sucesso');
      setEditMode(false);
    } catch (error) {
      toast.error('Erro ao atualizar missão');
    } finally {
      setUpdating(false);
    }
  };

  const generateReport = async () => {
    // If report already exists, just show it
    if (mission.ai_report_generated) {
      setGeneratingReport(true);
      try {
        const report = await api.get(`/missions/${id}/report`, token);
        setReportContent(report.content);
        setShowReportDialog(true);
      } catch (error) {
        toast.error('Erro ao carregar relatório');
      } finally {
        setGeneratingReport(false);
      }
      return;
    }
    
    // Generate new report
    setGeneratingReport(true);
    try {
      const result = await api.post(`/missions/${id}/generate-report`, {}, token);
      setReportContent(result.content);
      setShowReportDialog(true);
      await fetchData(); // Refresh to update ai_report_generated flag
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      toast.error(error.detail || 'Erro ao gerar relatório');
    } finally {
      setGeneratingReport(false);
    }
  };

  const exportToPDF = () => {
    if (!reportContent || !mission) return;
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;
    
    // Header
    doc.setFillColor(30, 58, 138); // Blue background
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PR APP - Relatório de Missão', margin, 15);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Código: ${mission.code}`, margin, 25);
    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, pageWidth - margin - 50, 25);
    
    yPosition = 50;
    doc.setTextColor(0, 0, 0);
    
    // Mission Info Box
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(margin, yPosition, maxWidth, 25, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Informações Gerais', margin + 5, yPosition + 7);
    doc.setFont('helvetica', 'normal');
    doc.text(`Cliente: ${mission.customer_name || 'N/A'}`, margin + 5, yPosition + 14);
    doc.text(`Fornecedor: ${mission.supplier_name || 'N/A'}`, margin + 80, yPosition + 14);
    doc.text(`Status: ${statusConfig[mission.status]?.label || mission.status}`, margin + 5, yPosition + 21);
    
    yPosition += 35;
    
    // Report Content
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    // Split content into lines that fit the page width
    const lines = doc.splitTextToSize(reportContent, maxWidth);
    
    for (let i = 0; i < lines.length; i++) {
      // Check if we need a new page
      if (yPosition > pageHeight - margin - 20) {
        doc.addPage();
        yPosition = margin;
        
        // Add header to new page
        doc.setFillColor(30, 58, 138);
        doc.rect(0, 0, pageWidth, 15, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text(`PR APP - ${mission.code} - Página ${doc.getNumberOfPages()}`, margin, 10);
        doc.setTextColor(0, 0, 0);
        yPosition = 25;
      }
      
      // Check if line is a header (starts with ## or #)
      const line = lines[i];
      if (line.startsWith('##') || line.startsWith('#')) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        yPosition += 3; // Extra space before headers
      } else if (line.startsWith('**') && line.endsWith('**')) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
      }
      
      // Clean up markdown formatting
      const cleanLine = line
        .replace(/^#+\s*/, '')
        .replace(/\*\*/g, '')
        .replace(/\*/g, '');
      
      doc.text(cleanLine, margin, yPosition);
      yPosition += 6;
    }
    
    // Footer on last page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Relatório gerado automaticamente por IA em ${new Date().toLocaleString('pt-BR')}`,
      margin,
      pageHeight - 10
    );
    
    // Save PDF
    doc.save(`relatorio_${mission.code}.pdf`);
    toast.success('PDF exportado com sucesso!');
  };

  const cancelMission = async () => {
    if (!cancelReason.trim()) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }
    setUpdating(true);
    try {
      await api.post(`/missions/${id}/cancel`, { reason: cancelReason }, token);
      await fetchData();
      toast.success('Missão cancelada');
      setShowCancelDialog(false);
      setCancelReason('');
    } catch (error) {
      toast.error('Erro ao cancelar missão');
    } finally {
      setUpdating(false);
    }
  };

  const contestMission = async () => {
    if (!contestReason.trim()) {
      toast.error('Informe o motivo da contestação');
      return;
    }
    setUpdating(true);
    try {
      await api.post(`/missions/${id}/contest`, { reason: contestReason }, token);
      await fetchData();
      toast.success('Missão contestada');
      setShowContestDialog(false);
      setContestReason('');
    } catch (error) {
      toast.error('Erro ao contestar missão');
    } finally {
      setUpdating(false);
    }
  };

  const approveMission = async () => {
    setUpdating(true);
    try {
      await api.post(`/missions/${id}/approve`, {}, token);
      await fetchData();
      toast.success('Missão aprovada para faturamento');
    } catch (error) {
      toast.error('Erro ao aprovar missão');
    } finally {
      setUpdating(false);
    }
  };

  const submitRating = async () => {
    if (!ratingValue) { toast.error('Selecione uma nota'); return; }
    if (ratingValue <= 3 && !ratingComment.trim()) {
      toast.error('Comentário obrigatório para notas iguais ou inferiores a 3');
      return;
    }
    setSubmittingRating(true);
    try {
      await api.post(`/missions/${id}/rate-prestador`, { rating: ratingValue, comment: ratingComment }, token);
      toast.success('Avaliação registrada com sucesso!');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar avaliação');
    } finally {
      setSubmittingRating(false);
    }
  };

  const invoiceMission = async () => {
    setUpdating(true);
    try {
      await api.post(`/missions/${id}/invoice`, {}, token);
      await fetchData();
      toast.success('Missão faturada com sucesso');
    } catch (error) {
      toast.error('Erro ao faturar missão');
    } finally {
      setUpdating(false);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await api.post(`/whatsapp/chat/${id}/send?content=${encodeURIComponent(newMessage)}`, {}, token);
      setNewMessage('');
      
      // Add message locally for immediate feedback
      setChatMessages(prev => [...prev, {
        id: Date.now().toString(),
        sender_type: 'platform',
        sender_name: user?.name || 'Você',
        content: newMessage,
        created_at: new Date().toISOString()
      }]);
      
      toast.success('Mensagem enviada');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const requestEndMission = async () => {
    setSendingMessage(true);
    try {
      // Use the new request-evidence endpoint
      await api.post(`/whatsapp/request-evidence/${id}`, {}, token);
      await fetchData();
      toast.success('Solicitação de evidências enviada');
    } catch (error) {
      toast.error(error.detail || 'Erro ao solicitar evidências');
    } finally {
      setSendingMessage(false);
    }
  };

  const finalizeMission = async () => {
    // Check if evidence was requested
    if (!mission.evidence_requested) {
      toast.error('Deve solicitar evidências antes de finalizar');
      return;
    }
    
    setUpdating(true);
    try {
      // Use the new finalize endpoint
      await api.post(`/missions/${id}/finalize`, {}, token);
      await fetchData();
      toast.success('Missão finalizada com sucesso');
    } catch (error) {
      toast.error(error.detail || 'Erro ao finalizar missão');
    } finally {
      setUpdating(false);
    }
  };

  const saveOdometerData = async () => {
    setSavingOdometer(true);
    try {
      const updateData = {};
      
      if (odometerStart) updateData.odometer_start = parseFloat(odometerStart);
      if (odometerEnd) updateData.odometer_end = parseFloat(odometerEnd);
      if (arrivedAt) updateData.supplier_arrived_at = new Date(arrivedAt).toISOString();
      
      // Calculate total_km if both values exist
      if (odometerStart && odometerEnd) {
        updateData.total_km = Math.max(0, parseFloat(odometerEnd) - parseFloat(odometerStart));
      }
      
      await api.put(`/missions/${id}`, updateData, token);
      await fetchData();
      toast.success('Dados de hodômetro salvos');
    } catch (error) {
      toast.error('Erro ao salvar dados');
    } finally {
      setSavingOdometer(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
  };


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!mission) {
    return null;
  }

  const config = statusConfig[mission.status];
  const canEdit = hasRole(['mestre', 'adm', 'gestor_operacional']);
  const canApprove = hasRole(['mestre', 'adm', 'gestor_operacional']) && mission.status === 'aguardando_validacao';
  const canInvoice = hasRole(['mestre', 'adm', 'financeiro']) && mission.status === 'aprovado_faturamento';
  const canChat = mission.supplier_id && ['aguardando_inicio', 'em_andamento'].includes(mission.status);
  const canFinalize = mission.status === 'em_andamento' && hasRole(['mestre', 'adm', 'gestor_operacional']);

  return (
    <div className="space-y-6 pl-14" data-testid="mission-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/missions')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
              <span className="font-mono">{mission.code}</span>
            </h1>
            <Badge className={config?.color || 'bg-slate-100'}>
              {config?.label || mission.status}
            </Badge>
            {mission.contested && (
              <Badge className="bg-red-100 text-red-700">Contestada</Badge>
            )}
          </div>
          <p className="text-slate-500 mt-1">{mission.reason_description}</p>
        </div>
        <div className="flex items-center gap-2">
          {canChat && (
            <Badge className="bg-green-100 text-green-700 animate-pulse">
              <MessageSquare className="w-3 h-3 mr-1" />
              Chat Ativo
            </Badge>
          )}
          {user?.role === 'mestre' && (
            <Button variant="ghost" size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
              onClick={async () => {
                if (!window.confirm(`Excluir missão ${mission.code}? Esta ação não pode ser desfeita.`)) return;
                try {
                  await api.delete(`/missions/${mission.id}`, token);
                  toast.success('Missão excluída');
                  navigate('/missions');
                } catch (e) { toast.error('Erro ao excluir'); }
              }}
              data-testid="delete-mission-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Excluir
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 rounded-xl p-1">
          <TabsTrigger value="details" className="rounded-lg">
            <FileText className="w-4 h-4 mr-2" />
            Detalhes
          </TabsTrigger>
          {mission.supplier_id && (
            <TabsTrigger value="chat" className="rounded-lg" data-testid="chat-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat
              {chatMessages.filter(m => m.sender_type === 'supplier' || m.sender_type === 'prestador').filter(m => !m.read).length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">
                  {chatMessages.filter(m => (m.sender_type === 'supplier' || m.sender_type === 'prestador') && !m.read).length}
                </Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="timeline" className="rounded-lg">
            <Clock className="w-4 h-4 mr-2" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="report" className="rounded-lg" data-testid="report-tab">
            <FileDown className="w-4 h-4 mr-2" />
            Relatório
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mission Info */}
            <Card className="premium-card border-0">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
                  Informações da Missão
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-1" />
                  <div>
                    <p className="text-sm text-slate-500">Localização</p>
                    <p className="font-medium">{mission.location_address}</p>
                    <a 
                      href={`https://www.google.com/maps?q=${mission.location_lat},${mission.location_lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Ver no mapa
                    </a>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-slate-400 mt-1" />
                  <div>
                    <p className="text-sm text-slate-500">Veículo</p>
                    <p className="font-medium font-mono">{mission.vehicle_plate}</p>
                    <p className="text-sm text-slate-500">{mission.vehicle_type || 'Caminhão'}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-400 mt-1" />
                  <div>
                    <p className="text-sm text-slate-500">Motorista</p>
                    <p className="font-medium">{mission.driver_name}</p>
                    <p className="text-sm text-slate-500">{mission.driver_phone}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer & Supplier */}
            <Card className="premium-card border-0">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
                  Cliente & Fornecedor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-slate-400 mt-1" />
                  <div>
                    <p className="text-sm text-slate-500">Cliente</p>
                    <p className="font-medium">{mission.customer_name || '-'}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-slate-400 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Fornecedor</p>
                    {mission.supplier_name ? (
                      <>
                        <p className="font-medium">{mission.supplier_name}</p>
                        {mission.assigned_prestador_name && (
                          <p className="text-sm text-amber-700 mt-0.5" data-testid="assigned-prestador-name">
                            Prestador: {mission.assigned_prestador_name}
                            {mission.supplier_eta && ` • ETA: ${formatEta(mission.supplier_eta)}`}
                          </p>
                        )}
                      </>
                    ) : canEdit && mission.status === 'criada' ? (
                      <div className="space-y-4 mt-2">
                        <p className="text-xs text-slate-400">
                          Utilize o fluxo de &quot;Nova Missão&quot; para notificar fornecedores, ou selecione manualmente abaixo.
                        </p>
                        
                        {/* Waiting for responses indicator */}
                        {mission.whatsapp_notified_at && supplierResponses.length === 0 && (
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 text-yellow-600 animate-spin" />
                              <span className="text-sm text-yellow-700">
                                Aguardando respostas dos fornecedores...
                              </span>
                            </div>
                            <p className="text-xs text-yellow-600 mt-1">
                              Notificação enviada às {formatTime(mission.whatsapp_notified_at)}
                            </p>
                          </div>
                        )}
                        
                        {/* Supplier Responses */}
                        {supplierResponses.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-slate-700">Respostas dos Prestadores:</p>
                              <span className="text-xs bg-slate-100 px-2 py-1 rounded-full">
                                {supplierResponses.filter(r => r.available).length} disponíveis / {supplierResponses.length} respostas
                              </span>
                            </div>
                            {supplierResponses.map((resp) => (
                              <div 
                                key={resp.prestador_id} 
                                className={`p-3 rounded-lg border ${
                                  resp.available 
                                    ? 'bg-green-50 border-green-200' 
                                    : 'bg-red-50 border-red-200'
                                }`}
                                data-testid={`supplier-response-${resp.prestador_id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">{resp.prestador_name}</p>
                                    <p className="text-xs text-slate-500">{resp.supplier_name}</p>
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                      <span className="text-xs font-medium text-slate-600">{(resp.rating_avg || 0).toFixed(1)}</span>
                                      <span className="text-xs text-slate-400">({resp.rating_count || 0})</span>
                                    </div>
                                    {resp.available ? (
                                      <div className="flex items-center gap-1 mt-1">
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                        <span className="text-sm text-green-700 font-medium">Disponível</span>
                                        {resp.eta_text && (
                                          <span className="text-sm text-slate-600 ml-2">
                                            • ETA: {formatEta(resp.eta_text)}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 mt-1">
                                        <AlertCircle className="w-4 h-4 text-red-600" />
                                        <span className="text-sm text-red-700">Indisponível</span>
                                      </div>
                                    )}
                                    {resp.responded_at && (
                                      <p className="text-xs text-slate-400 mt-1">
                                        Respondeu às {formatTime(resp.responded_at)}
                                      </p>
                                    )}
                                  </div>
                                  {resp.available && (
                                    <Button 
                                      onClick={() => selectSupplierFromResponse(resp.prestador_id)}
                                      disabled={updating}
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      data-testid={`select-supplier-${resp.prestador_id}`}
                                    >
                                      {updating ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        'Selecionar'
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Manual Selection */}
                        <div className="pt-2 border-t">
                          <p className="text-xs text-slate-500 mb-2">Ou selecione manualmente:</p>
                          <div className="flex items-center gap-2">
                            <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                              <SelectTrigger className="w-full" data-testid="manual-supplier-select">
                                <SelectValue placeholder="Selecione um fornecedor" />
                              </SelectTrigger>
                              <SelectContent>
                                {suppliers.map((s) => (
                                  <SelectItem key={s.id} value={s.id} data-testid={`manual-supplier-option-${s.id}`}>{s.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button onClick={assignSupplier} disabled={updating} size="sm" data-testid="assign-supplier-btn">
                              {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Atribuir'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-500">Não atribuído</p>
                    )}
                  </div>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-slate-400 mt-1" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Custo (Fornecedor)</p>
                        <p className="font-medium text-red-600">{formatCurrency(mission.supplier_cost)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Receita (Cliente)</p>
                        <p className="font-medium text-green-600">{formatCurrency(mission.customer_revenue)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <Card className="premium-card border-0">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
                Ações
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {/* Em Andamento: Finalizar apenas quando prestador concluiu todas etapas, ou Cancelar */}
                {canFinalize && (
                  <>
                    {mission.finished_at ? (
                      <Button 
                        onClick={finalizeMission} 
                        disabled={updating}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <StopCircle className="w-4 h-4 mr-2" />
                        {updating ? 'Finalizando...' : 'Concluir Missão'}
                      </Button>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 py-2 px-3" data-testid="awaiting-prestador-badge">
                        <Clock className="w-4 h-4 mr-2" />
                        Aguardando prestador concluir etapas
                      </Badge>
                    )}
                    <Button 
                      onClick={() => setShowCancelDialog(true)} 
                      disabled={updating}
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="cancel-mission-btn"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Cancelar Missão
                    </Button>
                  </>
                )}

                {/* Finalizada: Enviar para Validação e Gerar Relatório */}
                {mission.status === 'finalizada' && canEdit && (
                  <>
                    <Button 
                      onClick={generateReport} 
                      disabled={generatingReport}
                      variant="outline"
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {generatingReport ? 'Gerando...' : mission.ai_report_generated ? 'Ver Relatório' : 'Gerar Relatório IA'}
                    </Button>
                    <Button 
                      onClick={() => updateStatus('aguardando_validacao')} 
                      disabled={updating}
                      variant="outline"
                    >
                      Enviar para Validação
                    </Button>
                  </>
                )}

                {/* Aguardando Validação: Aprovar ou Contestar */}
                {mission.status === 'aguardando_validacao' && canApprove && (
                  <>
                    <Button 
                      onClick={approveMission} 
                      disabled={updating}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprovar para Faturamento
                    </Button>
                    <Button 
                      onClick={() => setShowContestDialog(true)} 
                      disabled={updating}
                      variant="outline"
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Contestar Missão
                    </Button>
                  </>
                )}

                {/* Contestada: Editar e Enviar para Validação */}
                {mission.status === 'contestada' && canEdit && (
                  <>
                    <Button 
                      onClick={openEditMode} 
                      disabled={updating}
                      variant="outline"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar Missão
                    </Button>
                    <Button 
                      onClick={() => updateStatus('aguardando_validacao')} 
                      disabled={updating}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Enviar para Validação
                    </Button>
                  </>
                )}

                {/* Aprovado: Faturar */}
                {canInvoice && (
                  <Button 
                    onClick={invoiceMission} 
                    disabled={updating}
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Faturar Missão
                  </Button>
                )}

                {/* Criada: Cancelar */}
                {canEdit && mission.status === 'criada' && (
                  <Button 
                    onClick={() => updateStatus('cancelada')} 
                    disabled={updating}
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Cancelar Missão
                  </Button>
                )}
              </div>

              {/* Info about Contest */}
              {mission.contested && mission.contest_reason && (
                <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="font-medium text-amber-800">Motivo da Contestação:</p>
                  <p className="text-amber-700 mt-1">{mission.contest_reason}</p>
                </div>
              )}

              {/* Info about Cancellation */}
              {mission.status === 'cancelada' && mission.cancel_reason && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200">
                  <p className="font-medium text-red-800">Motivo do Cancelamento:</p>
                  <p className="text-red-700 mt-1">{mission.cancel_reason}</p>
                  {mission.cancel_has_cost === false && (
                    <Badge className="mt-2 bg-green-100 text-green-700">Sem custo (dentro do prazo)</Badge>
                  )}
                  {mission.cancel_has_cost === true && (
                    <Badge className="mt-2 bg-red-100 text-red-700">Com custo de cancelamento</Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Prestador Rating Card */}
          {mission.assigned_prestador_id && ['finalizada', 'aguardando_validacao', 'aprovado_faturamento', 'faturado'].includes(mission.status) && (
            <Card className="premium-card border-0" data-testid="prestador-rating-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
                    <Star className="w-5 h-5 text-amber-500" />
                    Avaliação do Prestador
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setShowPrestadorProfile(true)} data-testid="view-prestador-profile-btn">
                    <Eye className="w-4 h-4 mr-2" /> Ver Perfil
                  </Button>
                </div>
                <CardDescription>{mission.assigned_prestador_name}</CardDescription>
              </CardHeader>
              <CardContent>
                {mission.rating != null ? (
                  <div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star key={i} className={`w-6 h-6 ${i <= mission.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    {mission.rating_comment && <p className="text-sm text-slate-600 mt-2">{mission.rating_comment}</p>}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-1" data-testid="rating-stars">
                      {[1, 2, 3, 4, 5].map(i => (
                        <button key={i} type="button" onClick={() => setRatingValue(i)} data-testid={`rating-star-${i}`}>
                          <Star className={`w-8 h-8 transition-colors ${i <= ratingValue ? 'text-amber-400 fill-amber-400' : 'text-slate-200 hover:text-amber-200'}`} />
                        </button>
                      ))}
                    </div>
                    {ratingValue > 0 && ratingValue <= 3 && (
                      <Textarea
                        value={ratingComment}
                        onChange={(e) => setRatingComment(e.target.value)}
                        placeholder="Descreva o motivo da nota baixa (obrigatório)"
                        data-testid="rating-comment-input"
                      />
                    )}
                    <Button onClick={submitRating} disabled={submittingRating || !ratingValue} className="bg-amber-500 hover:bg-amber-600" data-testid="submit-rating-btn">
                      {submittingRating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
                      Enviar Avaliação
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Dialog open={showPrestadorProfile} onOpenChange={setShowPrestadorProfile}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prestador-profile-modal">
              <DialogHeader>
                <DialogTitle>Perfil do Prestador — {mission.assigned_prestador_name}</DialogTitle>
              </DialogHeader>
              {mission.assigned_prestador_id && showPrestadorProfile && (
                <PrestadorProfileView prestadorId={mission.assigned_prestador_id} mode="admin" />
              )}
            </DialogContent>
          </Dialog>

          {/* Odometer & Calculated Data Card - Read only (filled by prestador) */}
          {mission.supplier_id && ['em_andamento', 'finalizada', 'aguardando_validacao', 'aprovado_faturamento', 'faturado'].includes(mission.status) && (
            <Card className="premium-card border-0">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
                  <Gauge className="w-5 h-5 text-blue-600" />
                  Dados Operacionais
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-600">KM Inicial</span>
                    </div>
                    <p className="text-xl font-bold text-blue-900 font-mono">
                      {mission.odometer_start ? `${mission.odometer_start}` : '—'}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-purple-600">KM Final</span>
                    </div>
                    <p className="text-xl font-bold text-purple-900 font-mono">
                      {mission.odometer_end ? `${mission.odometer_end}` : '—'}
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600">Total KM</span>
                    </div>
                    <p className="text-xl font-bold text-green-900 font-mono">
                      {mission.total_km ? `${mission.total_km}` : '—'}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-600">Tempo Total</span>
                    </div>
                    <p className="text-xl font-bold text-amber-900 font-mono">
                      {mission.total_time_minutes ? `${Math.floor(mission.total_time_minutes / 60)}h${mission.total_time_minutes % 60}m` : '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat" className="space-y-4">
          {/* Mission Steps Cards */}
          {mission.supplier_id && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Dados da Missão</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
                    <span className="text-blue-500 font-medium">Solicitação</span>
                    <p className="font-semibold text-slate-800 mt-1">
                      {formatDate(mission.whatsapp_notified_at || mission.created_at)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                    <span className="text-amber-500 font-medium">Previsão Chegada</span>
                    <p className="font-semibold text-slate-800 mt-1">{mission.supplier_eta ? formatDate(mission.supplier_eta) : '—'}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-50 border border-purple-100">
                    <span className="text-purple-500 font-medium">Confirmação / Início</span>
                    <p className="font-semibold text-slate-800 mt-1">
                      {formatDate(mission.supplier_accepted_at || mission.started_at)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-100">
                    <span className="text-indigo-500 font-medium">KM Inicial</span>
                    <p className="font-semibold text-slate-800 mt-1">{mission.odometer_start ? `${mission.odometer_start} km` : '—'}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-cyan-50 border border-cyan-100">
                    <span className="text-cyan-500 font-medium">Chegada no Local</span>
                    <p className="font-semibold text-slate-800 mt-1">
                      {formatDate(mission.supplier_arrived_at)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-orange-50 border border-orange-100">
                    <span className="text-orange-500 font-medium">Fim Atendimento</span>
                    <p className="font-semibold text-slate-800 mt-1">
                      {formatDate(mission.finished_at)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-100">
                    <span className="text-rose-500 font-medium">KM Final</span>
                    <p className="font-semibold text-slate-800 mt-1">{mission.odometer_end ? `${mission.odometer_end} km` : '—'}</p>
                  </div>
                  {(mission.total_km > 0 || mission.total_time_minutes > 0) && (
                    <div className="p-3 rounded-xl bg-green-50 border border-green-100">
                      <span className="text-green-500 font-medium">Totais</span>
                      <p className="font-semibold text-slate-800 mt-1">
                        {mission.total_km ? `${mission.total_km} km` : ''}
                        {mission.total_km && mission.total_time_minutes ? ' · ' : ''}
                        {mission.total_time_minutes ? `${Math.floor(mission.total_time_minutes / 60)}h${mission.total_time_minutes % 60}min` : ''}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Chat & Files Sub-tabs */}
          <Card className="premium-card border-0">
            <CardHeader className="pb-0">
              <div className="flex border-b">
                <button
                  onClick={() => setChatSubTab('messages')}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${chatSubTab === 'messages' ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                  data-testid="sub-tab-messages"
                >
                  <MessageSquare className="w-4 h-4 inline mr-1.5" />
                  Mensagens
                </button>
                <button
                  onClick={() => setChatSubTab('files')}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${chatSubTab === 'files' ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                  data-testid="sub-tab-files"
                >
                  <Image className="w-4 h-4 inline mr-1.5" />
                  Arquivos / Evidências
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {chatSubTab === 'messages' ? (
                <>
                  <ScrollArea className="h-96 border rounded-xl p-4 bg-slate-50 mt-3">
                    {chatMessages.filter(m => !m.has_media && !['km_start', 'km_end', 'evidence', 'evidence_done'].includes(m.step)).length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <MessageSquare className="w-12 h-12 mb-2" />
                        <p>Nenhuma mensagem ainda</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {chatMessages
                          .filter(m => !m.has_media && !['km_start', 'km_end', 'evidence', 'evidence_done'].includes(m.step))
                          .map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_type === 'platform' || msg.sender_type === 'api' ? 'justify-end' : msg.sender_type === 'system' ? 'justify-center' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                                msg.sender_type === 'platform' || msg.sender_type === 'api'
                                  ? 'bg-slate-900 text-white rounded-br-md'
                                  : msg.sender_type === 'system'
                                  ? 'bg-slate-100 text-slate-500 text-xs italic text-center'
                                  : msg.sender_type === 'prestador'
                                  ? 'bg-amber-100 border border-amber-200 rounded-bl-md'
                                  : 'bg-white border border-slate-200 rounded-bl-md'
                              }`}
                            >
                              {msg.sender_type !== 'system' && (
                                <p className={`text-xs mb-1 ${msg.sender_type === 'platform' || msg.sender_type === 'api' ? 'text-slate-300' : 'text-slate-500'}`}>
                                  {msg.sender_name}
                                </p>
                              )}
                              <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                              <p className={`text-[10px] mt-1 ${msg.sender_type === 'platform' || msg.sender_type === 'api' ? 'text-slate-400' : 'text-slate-400'}`}>
                                {formatDateShort(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={chatEndRef} />
                      </div>
                    )}
                  </ScrollArea>

                  {canChat && (
                    <div className="flex items-center gap-2 mt-4">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Digite sua mensagem..."
                        className="flex-1 rounded-xl"
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                        data-testid="chat-input"
                      />
                      <Button
                        onClick={sendChatMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="bg-amber-500 hover:bg-amber-600 rounded-xl"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-3 space-y-3">
                  {chatMessages.filter(m => m.has_media || ['km_start', 'km_end', 'evidence', 'evidence_done'].includes(m.step)).length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                      <Image className="w-12 h-12 mb-2" />
                      <p>Nenhum arquivo enviado ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {chatMessages
                        .filter(m => m.has_media || ['km_start', 'km_end', 'evidence'].includes(m.step))
                        .filter(m => m.file_id)
                        .map((msg) => (
                        <div key={msg.id} className="rounded-xl bg-slate-50 border overflow-hidden">
                          <div className="p-3 flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                              {msg.media_type?.startsWith('video') ? (
                                <Play className="w-5 h-5 text-amber-600" />
                              ) : msg.media_type?.startsWith('audio') ? (
                                <Volume2 className="w-5 h-5 text-amber-600" />
                              ) : (
                                <Image className="w-5 h-5 text-amber-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800">{msg.content}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-slate-400">{msg.sender_name}</span>
                                <span className="text-xs text-slate-300">·</span>
                                <span className="text-xs text-slate-400">{formatDate(msg.created_at)}</span>
                                {msg.step && (
                                  <>
                                    <span className="text-xs text-slate-300">·</span>
                                    <Badge className="bg-slate-100 text-slate-600 text-[10px]">{msg.step}</Badge>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button size="sm" variant="outline" className="text-xs rounded-lg"
                              onClick={() => {
                                const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${msg.file_id}?auth=${token}`;
                                window.open(url, '_blank');
                              }}
                              data-testid={`view-file-${msg.file_id}`}>
                              <Eye className="w-3 h-3 mr-1" /> Ver
                            </Button>
                          </div>
                          {/* Inline image preview */}
                          {msg.file_id && (!msg.media_type || msg.media_type?.startsWith('image')) && (
                            <div className="px-3 pb-3">
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${msg.file_id}?auth=${token}`}
                                alt={msg.content}
                                className="w-full max-h-64 object-contain rounded-lg bg-white border cursor-pointer"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${msg.file_id}?auth=${token}`, '_blank')}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      {/* Show evidence_done summary if exists */}
                      {chatMessages.filter(m => m.step === 'evidence_done').map(msg => (
                        <div key={msg.id} className="p-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          {msg.content}
                          <span className="text-xs text-green-500 ml-auto">{formatDate(msg.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card className="premium-card border-0">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
                Timeline da Missão
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Missão Criada */}
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5" />
                  <div>
                    <p className="font-medium">Missão Criada</p>
                    <p className="text-sm text-slate-500">{formatDate(mission.created_at)}</p>
                  </div>
                </div>
                
                {/* Fornecedores Notificados */}
                {mission.whatsapp_notified_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5" />
                    <div>
                      <p className="font-medium">Fornecedores Notificados</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.whatsapp_notified_at)}</p>
                    </div>
                  </div>
                )}
                
                {/* Fornecedor Aceito */}
                {mission.supplier_accepted_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mt-1.5" />
                    <div>
                      <p className="font-medium">Fornecedor Aceito: {mission.supplier_name}</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.supplier_accepted_at)}</p>
                    </div>
                  </div>
                )}

                {/* Previsão de Chegada (ETA do WhatsApp) */}
                {mission.supplier_eta && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-purple-500 mt-1.5" />
                    <div>
                      <p className="font-medium">Previsão de Chegada (informada pelo fornecedor)</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.supplier_eta)}</p>
                    </div>
                  </div>
                )}

                {/* Missão Iniciada (baseado no modelo de cobrança) */}
                {mission.started_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-orange-500 mt-1.5" />
                    <div>
                      <p className="font-medium">Missão Iniciada</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.started_at)}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {mission.charge_from_arrival ? 
                          '(Cobrança a partir da chegada na origem)' : 
                          '(Cobrança a partir do aceite da missão)'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Chegada na Origem */}
                {mission.supplier_arrived_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-teal-500 mt-1.5" />
                    <div>
                      <p className="font-medium">Chegada na Origem</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.supplier_arrived_at)}</p>
                    </div>
                  </div>
                )}

                {/* Missão Finalizada */}
                {mission.finished_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-green-600 mt-1.5" />
                    <div>
                      <p className="font-medium">Missão Finalizada</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.finished_at)}</p>
                    </div>
                  </div>
                )}

                {mission.approved_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 mt-1.5" />
                    <div>
                      <p className="font-medium">Aprovado para Faturamento</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.approved_at)}</p>
                    </div>
                  </div>
                )}

                {mission.invoiced_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-slate-500 mt-1.5" />
                    <div>
                      <p className="font-medium">Faturado</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.invoiced_at)}</p>
                    </div>
                  </div>
                )}

                {/* Cancelada */}
                {mission.status === 'cancelada' && mission.cancelled_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5" />
                    <div>
                      <p className="font-medium text-red-700">Missão Cancelada</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.cancelled_at)}</p>
                      {mission.cancel_reason && (
                        <p className="text-sm text-red-600 mt-1">Motivo: {mission.cancel_reason}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Contestada */}
                {mission.contested && mission.contested_at && (
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-amber-500 mt-1.5" />
                    <div>
                      <p className="font-medium text-amber-700">Missão Contestada</p>
                      <p className="text-sm text-slate-500">{formatDate(mission.contested_at)}</p>
                      {mission.contest_reason && (
                        <p className="text-sm text-amber-600 mt-1">Motivo: {mission.contest_reason}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report Tab */}
        <TabsContent value="report" className="space-y-4">
          <Card className="premium-card border-0">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
                <FileDown className="w-5 h-5 text-slate-400" />
                Relatório Operacional
              </CardTitle>
              <CardDescription>
                Relatório em PDF com todas as informações operacionais da missão
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-xl bg-slate-50 border text-sm text-slate-600 space-y-2">
                <p>O relatório inclui:</p>
                <ul className="list-disc list-inside space-y-1 text-xs text-slate-500">
                  <li>Dados cadastrais completos (cliente, fornecedor, veículo, motorista)</li>
                  <li>Cronologia de datas e horários</li>
                  <li>Timeline dos eventos</li>
                  <li>Resumo das mensagens do chat</li>
                  <li>Lista de evidências registradas</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/missions/${mission.id}/operational-report`;
                    window.open(`${url}?auth=${token}`, '_blank');
                  }}
                  className="bg-slate-900 hover:bg-slate-800 rounded-xl"
                  data-testid="view-report-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Visualizar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missions/${mission.id}/operational-report`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `relatorio_${mission.code}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) { toast.error('Erro ao baixar relatório'); }
                  }}
                  className="rounded-xl"
                  data-testid="download-report-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Missão</DialogTitle>
            <DialogDescription>
              Informe o motivo do cancelamento. O fornecedor será notificado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Motivo do cancelamento..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="min-h-[100px]"
            />
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm">
              <p className="font-medium text-amber-800">Atenção:</p>
              <p className="text-amber-700 mt-1">
                Se o cancelamento ocorrer dentro do tempo mínimo configurado no fornecedor, 
                não haverá cobrança. Caso contrário, poderá haver custo de cancelamento.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Voltar
            </Button>
            <Button 
              onClick={cancelMission} 
              disabled={updating}
              className="bg-red-600 hover:bg-red-700"
            >
              {updating ? 'Cancelando...' : 'Confirmar Cancelamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contest Dialog */}
      <Dialog open={showContestDialog} onOpenChange={setShowContestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contestar Missão</DialogTitle>
            <DialogDescription>
              Informe o motivo da contestação. A missão poderá ser editada após a contestação.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Motivo da contestação..."
              value={contestReason}
              onChange={(e) => setContestReason(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContestDialog(false)}>
              Voltar
            </Button>
            <Button 
              onClick={contestMission} 
              disabled={updating}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {updating ? 'Contestando...' : 'Confirmar Contestação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Mission Dialog */}
      <Dialog open={editMode} onOpenChange={setEditMode}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Missão Contestada</DialogTitle>
            <DialogDescription>
              Atualize as informações da missão e envie novamente para validação.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2">
              <Label>Descrição do Motivo</Label>
              <Textarea
                value={editFormData.reason_description}
                onChange={(e) => setEditFormData({...editFormData, reason_description: e.target.value})}
                placeholder="Descrição detalhada do problema..."
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Endereço</Label>
              <Input
                value={editFormData.location_address}
                onChange={(e) => setEditFormData({...editFormData, location_address: e.target.value})}
                placeholder="Endereço completo..."
              />
            </div>
            <div className="space-y-2">
              <Label>Placa do Veículo</Label>
              <Input
                value={editFormData.vehicle_plate}
                onChange={(e) => setEditFormData({...editFormData, vehicle_plate: e.target.value})}
                placeholder="ABC-1234"
              />
            </div>
            <div className="space-y-2">
              <Label>Cor do Veículo</Label>
              <Input
                value={editFormData.vehicle_color}
                onChange={(e) => setEditFormData({...editFormData, vehicle_color: e.target.value})}
                placeholder="Ex: Branco"
              />
            </div>
            <div className="space-y-2">
              <Label>Nome do Motorista</Label>
              <Input
                value={editFormData.driver_name}
                onChange={(e) => setEditFormData({...editFormData, driver_name: e.target.value})}
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefone do Motorista</Label>
              <Input
                value={editFormData.driver_phone}
                onChange={(e) => setEditFormData({...editFormData, driver_phone: e.target.value})}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Observações</Label>
              <Textarea
                value={editFormData.observations}
                onChange={(e) => setEditFormData({...editFormData, observations: e.target.value})}
                placeholder="Observações adicionais..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMode(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={saveEditedMission} 
              disabled={updating}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {updating ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Relatório da Missão - {mission?.code}
            </DialogTitle>
            <DialogDescription>
              Relatório gerado por Inteligência Artificial
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="prose prose-sm max-w-none bg-slate-50 p-6 rounded-xl whitespace-pre-wrap font-mono text-sm">
              {reportContent}
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={exportToPDF}
              className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                const blob = new Blob([reportContent], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `relatorio_${mission?.code || 'missao'}.txt`;
                a.click();
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar TXT
            </Button>
            <Button onClick={() => setShowReportDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MissionDetailPage;
